import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Clock, ClipboardList, Check, X, Loader2, MessageCircle, Send, Bell, BellOff,
  User, Mail, Phone, MapPin, FileText, Calendar, Package, Timer, AlertTriangle, Building2, Skull,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import EmptyStateFlua from "@/components/EmptyStateFlua";
import {
  listOnboardingPendentes, validarOnboardingPendente,
  rejeitarOnboardingPendente, marcarOnboardingRespondido,
} from "@/lib/api";
import { fmtInt } from "@/lib/format";

const SLA_MIN = 30;
const NOTIF_STORAGE_KEY = "flua_onboarding_notify";

const statusBadge = (item) => {
  if (item.status === "validado") return { label: "Virou cliente", cls: "bg-emerald-500/15 text-emerald-500 border-emerald-500/40" };
  if (item.status === "rejeitado") return { label: "Rejeitado", cls: "bg-red-500/15 text-red-500 border-red-500/40" };
  if (item.is_overdue) return { label: "Vencido", cls: "bg-red-500/15 text-red-500 border-red-500/40" };
  return { label: "Aguardando", cls: "bg-amber-500/15 text-amber-500 border-amber-500/40" };
};

// Componente que renderiza o countdown SLA com update por segundo.
function SLACountdown({ submittedAt, deadlineAt, respondedAt, status }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (status !== "pendente" || respondedAt) return; // já parado
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [status, respondedAt]);

  const sub = submittedAt ? new Date(submittedAt).getTime() : 0;
  const stop = respondedAt ? new Date(respondedAt).getTime() : now;
  const elapsedSec = Math.max(0, Math.floor((stop - sub) / 1000));
  const deadline = deadlineAt ? new Date(deadlineAt).getTime() : sub + SLA_MIN * 60_000;
  const remainingSec = Math.floor((deadline - stop) / 1000);
  const dentroSla = elapsedSec <= SLA_MIN * 60;

  const mm = String(Math.floor(Math.abs(remainingSec) / 60)).padStart(2, "0");
  const ss = String(Math.abs(remainingSec) % 60).padStart(2, "0");

  if (respondedAt || status !== "pendente") {
    // Total decorrido até responder/validar
    const em = String(Math.floor(elapsedSec / 60)).padStart(2, "0");
    const es = String(elapsedSec % 60).padStart(2, "0");
    return (
      <Badge variant="outline" className={dentroSla ? "border-emerald-500/40 text-emerald-500" : "border-red-500/40 text-red-500"}>
        <Timer className="w-3 h-3 mr-1" /> Respondeu em {em}:{es}
      </Badge>
    );
  }

  // Pendente
  if (remainingSec >= 0) {
    const color = remainingSec < 600 ? "text-red-500 border-red-500/50" : remainingSec < 1500 ? "text-amber-500 border-amber-500/50" : "text-emerald-500 border-emerald-500/50";
    return (
      <Badge variant="outline" className={`${color} tabular-nums`}>
        <Clock className="w-3 h-3 mr-1" /> {mm}:{ss} restantes
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="border-red-500/50 text-red-500 tabular-nums">
      <AlertTriangle className="w-3 h-3 mr-1" /> Atrasado {mm}:{ss}
    </Badge>
  );
}

const digitsOnly = (v) => String(v || "").replace(/\D/g, "");
const cpfMask = (v) => {
  const d = digitsOnly(v).slice(0, 14);
  if (d.length <= 11) return d.replace(/(\d{3})(\d)/, "$1.$2").replace(/(\d{3})\.(\d{3})(\d)/, "$1.$2.$3").replace(/(\d{3})\.(\d{3})\.(\d{3})(\d)/, "$1.$2.$3-$4");
  return d.replace(/(\d{2})(\d)/, "$1.$2").replace(/(\d{2})\.(\d{3})(\d)/, "$1.$2.$3").replace(/(\d{3})(\d{4})(\d)/, "$1/$2-$3");
};
const phoneMask = (v) => {
  const d = digitsOnly(v).slice(0, 11);
  if (d.length <= 10) return d.replace(/(\d{2})(\d)/, "($1) $2").replace(/(\d{4})(\d)/, "$1-$2");
  return d.replace(/(\d{2})(\d)/, "($1) $2").replace(/(\d{5})(\d)/, "$1-$2");
};

export default function OnboardingPendentesCard() {
  const [pendentes, setPendentes] = useState([]);
  const [vencidos, setVencidos] = useState([]);
  const [validados, setValidados] = useState([]);
  const [rejeitados, setRejeitados] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("pendente");
  const [detailItem, setDetailItem] = useState(null);
  const [rejectItem, setRejectItem] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [acting, setActing] = useState("");
  const [notifPerm, setNotifPerm] = useState(() =>
    typeof Notification !== "undefined" ? Notification.permission : "unsupported"
  );
  const [notifOff, setNotifOff] = useState(() =>
    typeof localStorage !== "undefined" && localStorage.getItem(NOTIF_STORAGE_KEY) === "off"
  );
  const lastPendingIdsRef = useRef(new Set());
  const firstLoadRef = useRef(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [pend, venc, val, rej] = await Promise.all([
        listOnboardingPendentes("pendente"),
        listOnboardingPendentes("vencido"),
        listOnboardingPendentes("validado"),
        listOnboardingPendentes("rejeitado"),
      ]);
      setPendentes(Array.isArray(pend) ? pend : []);
      setVencidos(Array.isArray(venc) ? venc : []);
      setValidados(Array.isArray(val) ? val : []);
      setRejeitados(Array.isArray(rej) ? rej : []);
    } catch (err) {
      console.error("Erro ao carregar pendentes:", err);
      toast.error("Falha ao carregar formulários pendentes");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Poll a cada 45s para detectar novas submissões e disparar toast+push.
  useEffect(() => {
    const t = setInterval(load, 45000);
    return () => clearInterval(t);
  }, [load]);

  // Dispara Notification API do navegador (se autorizado).
  const fireBrowserNotification = useCallback((item, extraCount) => {
    if (typeof Notification === "undefined") return;
    if (Notification.permission !== "granted") return;
    if (notifOff) return;
    try {
      const body = extraCount > 0
        ? `${item.nome_cliente} + ${extraCount} outro${extraCount > 1 ? "s" : ""} · plano ${item.plano_nome}. Você tem 30 min pra responder.`
        : `${item.nome_cliente} · plano ${item.plano_nome}. Você tem 30 min pra responder.`;
      const n = new Notification("📥 Novo formulário FLUA", {
        body,
        tag: `flua-onboard-${item.id}`,
        icon: "https://customer-assets-agu9un31.emergentagent.net/job_sales-tracker-1336/artifacts/xrne8mgy_versao_branca_03.webp",
        badge: "https://customer-assets-agu9un31.emergentagent.net/job_sales-tracker-1336/artifacts/xrne8mgy_versao_branca_03.webp",
      });
      n.onclick = () => { window.focus(); n.close(); };
    } catch (e) {
      console.warn("Falha ao disparar notification:", e);
    }
  }, [notifOff]);

  // Toast + Push para novas pendências detectadas após o primeiro load.
  useEffect(() => {
    const currentIds = new Set(pendentes.map((i) => i.id));
    if (firstLoadRef.current) {
      lastPendingIdsRef.current = currentIds;
      firstLoadRef.current = false;
      return;
    }
    const newOnes = [...currentIds].filter((id) => !lastPendingIdsRef.current.has(id));
    if (newOnes.length > 0) {
      const first = pendentes.find((p) => p.id === newOnes[0]);
      if (first) {
        toast.success(
          `📥 Novo formulário: ${first.nome_cliente}`,
          { description: `${newOnes.length > 1 ? `+${newOnes.length - 1} outros — ` : ""}você tem ${SLA_MIN} min para responder.` }
        );
        fireBrowserNotification(first, newOnes.length - 1);
      }
    }
    lastPendingIdsRef.current = currentIds;
  }, [pendentes, fireBrowserNotification]);

  const toggleNotifications = async () => {
    if (typeof Notification === "undefined") {
      toast.error("Este navegador não suporta notificações");
      return;
    }
    if (Notification.permission === "granted") {
      const nextOff = !notifOff;
      localStorage.setItem(NOTIF_STORAGE_KEY, nextOff ? "off" : "on");
      setNotifOff(nextOff);
      toast.success(nextOff ? "Notificações silenciadas" : "Notificações ativadas");
      return;
    }
    if (Notification.permission === "denied") {
      toast.error("Permissão negada — desbloqueie nas configurações do navegador");
      return;
    }
    const p = await Notification.requestPermission();
    setNotifPerm(p);
    if (p === "granted") {
      localStorage.setItem(NOTIF_STORAGE_KEY, "on");
      setNotifOff(false);
      toast.success("Notificações ativadas! Você receberá alerta a cada novo formulário.");
      // Fire a test notification imediatamente pra confirmar
      try {
        new Notification("✅ Notificações FLUA ativas", {
          body: "Você será avisado a cada novo formulário do onboarding.",
          tag: "flua-notif-test",
        });
      } catch { /* ignore */ }
    } else {
      toast.error("Notificações não ativadas");
    }
  };

  const notifButton = useMemo(() => {
    if (notifPerm === "unsupported") return null;
    if (notifPerm === "denied") {
      return { icon: BellOff, label: "Notificações bloqueadas", cls: "text-red-500 border-red-500/40" };
    }
    if (notifPerm === "granted" && !notifOff) {
      return { icon: Bell, label: "Alertas ativos", cls: "text-emerald-500 border-emerald-500/40" };
    }
    if (notifPerm === "granted" && notifOff) {
      return { icon: BellOff, label: "Alertas silenciados", cls: "text-muted-foreground border-border" };
    }
    return { icon: Bell, label: "Ativar alertas do navegador", cls: "text-primary border-primary/40 animate-pulse" };
  }, [notifPerm, notifOff]);

  const counts = useMemo(() => ({
    pendente: pendentes.length,
    vencido: vencidos.length,
    validado: validados.length,
    rejeitado: rejeitados.length,
  }), [pendentes.length, vencidos.length, validados.length, rejeitados.length]);

  const filtered = useMemo(() => {
    if (tab === "vencido") return vencidos;
    if (tab === "validado") return validados;
    if (tab === "rejeitado") return rejeitados;
    return pendentes;
  }, [tab, pendentes, vencidos, validados, rejeitados]);

  const enviarLinkWhats = (item) => {
    const digits = digitsOnly(item.whatsapp);
    if (!digits) { toast.error("WhatsApp do cliente não informado"); return; }
    const texto = encodeURIComponent(
      `Olá ${item.nome_cliente}! Sou o(a) ${item.vendedor || "consultor(a) FLUA"}. ` +
      `Recebemos seu cadastro (${item.plano_nome}) e estamos enviando o link para assinatura do contrato. Qualquer dúvida, é só responder por aqui.`
    );
    window.open(`https://wa.me/55${digits}?text=${texto}`, "_blank", "noopener,noreferrer");
    marcarRespondido(item);
  };

  const updateInList = (id, patch) => {
    const upd = (list) => list.map((p) => (p.id === id ? { ...p, ...patch } : p));
    setPendentes(upd); setVencidos(upd); setValidados(upd); setRejeitados(upd);
  };

  const marcarRespondido = async (item) => {
    if (item.responded_at) return;
    try {
      const updated = await marcarOnboardingRespondido(item.id);
      updateInList(item.id, updated);
      toast.success("Marcado como respondido — tempo travado no ranking");
    } catch (err) {
      console.error(err);
      toast.error("Falha ao registrar resposta");
    }
  };

  const validar = async (item) => {
    setActing(item.id);
    try {
      await validarOnboardingPendente(item.id);
      toast.success(`${item.nome_cliente} virou cliente! ✓`);
      setDetailItem(null);
      await load();
      // Dispara evento pra ClientesTab recarregar a lista de clientes
      window.dispatchEvent(new CustomEvent("onboarding:validated"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao validar");
    } finally { setActing(""); }
  };

  const confirmarRejeicao = async () => {
    if (!rejectItem) return;
    setActing(rejectItem.id);
    try {
      await rejeitarOnboardingPendente(rejectItem.id, rejectReason);
      toast.success("Cadastro rejeitado");
      setRejectItem(null); setRejectReason("");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao rejeitar");
    } finally { setActing(""); }
  };

  return (
    <div
      className="rounded-2xl border-2 border-amber-500/50 bg-gradient-to-br from-amber-500/10 via-amber-500/5 to-transparent p-5 space-y-4 shadow-lg shadow-amber-500/5"
      data-testid="onboarding-pendentes-card"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-xl bg-amber-500/20 border border-amber-500/40 grid place-items-center shrink-0">
            <ClipboardList className="w-5 h-5 text-amber-500" />
          </div>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-heading font-bold text-lg">Formulários para validar</h3>
              {counts.pendente > 0 && (
                <Badge className="bg-red-500 text-white border-0 tabular-nums" data-testid="badge-pendentes-count">
                  {counts.pendente} pendente{counts.pendente > 1 ? "s" : ""}
                </Badge>
              )}
              {counts.vencido > 0 && (
                <Badge className="bg-red-700 text-white border-0 tabular-nums" data-testid="badge-vencidos-count">
                  <Skull className="w-3 h-3 mr-1" /> {counts.vencido} vencido{counts.vencido > 1 ? "s" : ""}
                </Badge>
              )}
            </div>
            <p className="text-xs text-muted-foreground mt-0.5 max-w-2xl">
              Cadastros recebidos pelo link público. <strong className="text-amber-500">Responda em até {SLA_MIN} min</strong> — envie o link de assinatura pelo WhatsApp e só depois clique em <em>Validar</em> para virar cliente.
            </p>
          </div>
        </div>
        {notifButton && (() => {
          const NotifIcon = notifButton.icon;
          return (
            <Button
              variant="outline"
              size="sm"
              onClick={toggleNotifications}
              className={notifButton.cls}
              data-testid="btn-toggle-notifications"
            >
              <NotifIcon className="w-3.5 h-3.5 mr-1.5" /> {notifButton.label}
            </Button>
          );
        })()}
      </div>

      {counts.vencido > 0 && tab !== "vencido" && (
        <div
          className="rounded-lg border-2 border-red-500/50 bg-red-500/10 p-3 flex items-center gap-3"
          data-testid="alert-vencidos"
        >
          <Skull className="w-5 h-5 text-red-500 shrink-0" />
          <div className="flex-1 text-sm">
            <strong className="text-red-500">{counts.vencido} cadastro{counts.vencido > 1 ? "s" : ""} passou{counts.vencido > 1 ? "" : "u"} de 24h sem resposta.</strong>
            <span className="text-muted-foreground ml-1">Trate agora — o cliente pode desistir.</span>
          </div>
          <Button size="sm" variant="outline" onClick={() => setTab("vencido")} className="text-red-500 border-red-500/40 shrink-0">
            Ver agora
          </Button>
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <TabsList className="grid grid-cols-4 w-full sm:w-auto">
          <TabsTrigger value="pendente" data-testid="tab-pendente">
            Pendentes <span className="ml-1.5 tabular-nums text-amber-500 font-bold">{counts.pendente}</span>
          </TabsTrigger>
          <TabsTrigger value="vencido" data-testid="tab-vencido">
            Vencidos <span className="ml-1.5 tabular-nums text-red-500 font-bold">{counts.vencido}</span>
          </TabsTrigger>
          <TabsTrigger value="validado" data-testid="tab-validado">
            Validados <span className="ml-1.5 tabular-nums text-emerald-500 font-bold">{counts.validado}</span>
          </TabsTrigger>
          <TabsTrigger value="rejeitado" data-testid="tab-rejeitado">
            Rejeitados <span className="ml-1.5 tabular-nums text-red-500 font-bold">{counts.rejeitado}</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value={tab} className="mt-4 space-y-2">
          {loading && (
            <div className="text-center py-8 text-muted-foreground flex items-center justify-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Carregando…
            </div>
          )}
          {!loading && filtered.length === 0 && (
            <EmptyStateFlua
              icon={ClipboardList}
              title={
                tab === "pendente" ? "Nenhum formulário pra validar agora" :
                tab === "vencido" ? "Nenhum vencido — parabéns pelo ritmo!" :
                tab === "validado" ? "Nenhum cadastro virou cliente ainda" :
                "Nenhum cadastro rejeitado"
              }
              description={
                tab === "pendente"
                  ? "Compartilhe o link acima com seus prospectos — quando alguém preencher, cai aqui em tempo real."
                  : tab === "vencido"
                    ? "Todos os cadastros foram respondidos dentro do SLA de 30 minutos."
                    : tab === "validado"
                      ? "Cadastros validados aparecem aqui pra consulta histórica."
                      : "Rejeições ficam arquivadas nesta aba pra referência futura."
              }
              compact
            />
          )}
          {filtered.map((it) => {
            const sb = statusBadge(it);
            const isVencido = it.is_overdue && it.status === "pendente";
            return (
              <div key={it.id}
                className={`rounded-lg border ${isVencido ? "border-red-500/40 bg-red-500/5" : "border-border bg-card/80"} p-3 flex flex-col md:flex-row md:items-center gap-3`}
                data-testid={`pendente-row-${it.id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold truncate">{it.nome_cliente}</span>
                    <Badge variant="outline" className={sb.cls}>{sb.label}</Badge>
                    <Badge variant="outline" className="text-[10px]">
                      <Package className="w-3 h-3 mr-1" /> {it.plano_nome}
                    </Badge>
                    {it.vendedor && (
                      <Badge variant="outline" className="text-[10px] text-primary border-primary/40">
                        <User className="w-3 h-3 mr-1" /> {it.vendedor}
                      </Badge>
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground mt-1 flex flex-wrap gap-x-3 gap-y-1">
                    {it.whatsapp && <span className="flex items-center gap-1"><Phone className="w-3 h-3" /> {it.whatsapp}</span>}
                    {it.email && <span className="flex items-center gap-1 truncate"><Mail className="w-3 h-3" /> {it.email}</span>}
                    {it.cpf && <span className="tabular-nums">{it.cpf}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <SLACountdown
                    submittedAt={it.submitted_at}
                    deadlineAt={it.deadline_at}
                    respondedAt={it.responded_at}
                    status={it.status}
                  />
                  <Button size="sm" variant="outline" onClick={() => setDetailItem(it)} data-testid={`btn-ver-${it.id}`}>
                    Ver dados
                  </Button>
                  {it.status === "pendente" && (
                    <>
                      <Button size="sm" variant="outline"
                        className="text-emerald-500 border-emerald-500/40 hover:bg-emerald-500/10"
                        onClick={() => enviarLinkWhats(it)}
                        data-testid={`btn-whats-${it.id}`}>
                        <MessageCircle className="w-3.5 h-3.5 mr-1" /> Enviar link
                      </Button>
                      <Button size="sm" onClick={() => validar(it)}
                        disabled={acting === it.id}
                        className="bg-emerald-500 hover:bg-emerald-500/90 text-white"
                        data-testid={`btn-validar-${it.id}`}>
                        {acting === it.id ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Check className="w-3.5 h-3.5 mr-1" />}
                        Validar
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setRejectItem(it)}
                        className="text-red-500 border-red-500/40 hover:bg-red-500/10"
                        data-testid={`btn-rejeitar-${it.id}`}>
                        <X className="w-3.5 h-3.5 mr-1" /> Rejeitar
                      </Button>
                    </>
                  )}
                  {it.status === "rejeitado" && it.rejection_reason && (
                    <span className="text-[11px] text-muted-foreground italic max-w-[220px] truncate" title={it.rejection_reason}>
                      "{it.rejection_reason}"
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </TabsContent>
      </Tabs>

      {/* Modal: detalhes read-only */}
      <Dialog open={!!detailItem} onOpenChange={(v) => !v && setDetailItem(null)}>
        <DialogContent className="max-w-lg" data-testid="dialog-pendente-detail">
          {detailItem && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <ClipboardList className="w-5 h-5 text-amber-500" />
                  Dados do cadastro
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-3 text-sm">
                <Row icon={User} label="Nome" value={detailItem.nome_cliente} />
                {detailItem.razao_social && <Row icon={Building2} label="Razão social" value={detailItem.razao_social} />}
                <Row icon={FileText} label="CPF/CNPJ" value={detailItem.cpf || "—"} mono />
                {detailItem.cep && <Row icon={MapPin} label="CEP" value={detailItem.cep} mono />}
                <Row icon={MapPin} label="Endereço" value={
                  [detailItem.endereco, detailItem.numero].filter(Boolean).join(", ") || "—"
                } />
                {detailItem.complemento && <Row label="Complemento" value={detailItem.complemento} />}
                {detailItem.condominio && <Row icon={Building2} label="Condomínio" value={detailItem.condominio} />}
                <Row icon={Mail} label="E-mail" value={detailItem.email || "—"} />
                <Row icon={Phone} label="WhatsApp" value={phoneMask(detailItem.whatsapp) || "—"} />
                {detailItem.telefone_fixo && <Row icon={Phone} label="Fixo" value={phoneMask(detailItem.telefone_fixo)} />}
                <Row icon={Package} label="Plano" value={`${detailItem.plano_nome} + ${detailItem.app_bonus_label}`} />
                <Row icon={Calendar} label="Vencimento" value={`Dia ${detailItem.dia_vencimento} de cada mês`} />
                <Row icon={User} label="Vendedor(a)" value={detailItem.vendedor || "—"} />
                <Row icon={Timer} label="Enviado em" value={new Date(detailItem.submitted_at).toLocaleString("pt-BR")} />
                {detailItem.responded_at && (
                  <Row icon={Send} label="Respondido em" value={new Date(detailItem.responded_at).toLocaleString("pt-BR")} />
                )}
              </div>
              <DialogFooter className="gap-2 sm:gap-2">
                <Button variant="outline" onClick={() => setDetailItem(null)}>Fechar</Button>
                {detailItem.status === "pendente" && (
                  <Button onClick={() => validar(detailItem)} disabled={acting === detailItem.id}
                    className="bg-emerald-500 hover:bg-emerald-500/90 text-white">
                    <Check className="w-4 h-4 mr-1" /> Validar e virar cliente
                  </Button>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal: rejeição com motivo */}
      <Dialog open={!!rejectItem} onOpenChange={(v) => { if (!v) { setRejectItem(null); setRejectReason(""); } }}>
        <DialogContent className="max-w-md" data-testid="dialog-rejeitar">
          {rejectItem && (
            <>
              <DialogHeader>
                <DialogTitle>Rejeitar cadastro de {rejectItem.nome_cliente}?</DialogTitle>
              </DialogHeader>
              <div className="space-y-2">
                <p className="text-xs text-muted-foreground">O cadastro ficará arquivado na aba "Rejeitados" para consulta futura. Descreva o motivo (opcional).</p>
                <Textarea
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder="Ex.: CPF já é cliente / fora da área de cobertura / número inválido…"
                  rows={3}
                  data-testid="input-motivo-rejeicao"
                />
              </div>
              <DialogFooter className="gap-2 sm:gap-2">
                <Button variant="outline" onClick={() => { setRejectItem(null); setRejectReason(""); }}>Cancelar</Button>
                <Button
                  onClick={confirmarRejeicao}
                  disabled={acting === rejectItem.id}
                  className="bg-red-500 hover:bg-red-500/90 text-white"
                  data-testid="btn-confirm-rejeitar"
                >
                  {acting === rejectItem.id ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <X className="w-4 h-4 mr-1" />}
                  Rejeitar
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Row({ icon: Icon, label, value, mono }) {
  return (
    <div className="flex items-start gap-2">
      {Icon && <Icon className="w-3.5 h-3.5 text-muted-foreground mt-0.5 shrink-0" />}
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
        <div className={`text-sm ${mono ? "tabular-nums" : ""}`}>{value}</div>
      </div>
    </div>
  );
}
